<script setup>
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from 'reka-ui';
import { computed } from 'vue';
import FormGroup from '@/Components/FormGroup.vue';
import JetButton from '@/Jetstream/Button.vue';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => [],
    },
    horizontal: {
        type: [Boolean, Object],
        default: true,
    },
    required: {
        type: Boolean,
        default: false,
    },
    min: {
        type: Number,
        default: 0,
    },
    max: {
        type: Number,
        default: 1440,
    },
    stepSize: {
        type: Number,
        default: 5,
    },
    label: {
        type: String,
        default: '',
    },
    formatValue: {
        type: Function,
        default: (value) => {
            if (!Array.isArray(value) || value.length !== 2) return '';

            const formatHHMM = (hhmm) => {
                const hours = Math.floor(hhmm / 60);
                const minutes = hhmm % 60;

                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
            };

            return `${formatHHMM(value[0])} - ${formatHHMM(value[1])}`;
        },
    },
    invalidFeedback: Object,
    fullWidthWithValueBelow: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

// Reka-ui cannot track which thumb is being dragged when it receives an empty
// or null model value, because getNextSortedValues produces a sparse array that
// re-indexes the thumbs on first move. Always feed it a valid two-element array
// so the right thumb stays the right thumb.
const sliderModelValue = computed(() =>
    Array.isArray(props.modelValue) && props.modelValue.length === 2 ? props.modelValue : [props.min, props.min],
);

const wrapperClasses = computed(() =>
    props.fullWidthWithValueBelow ? ['flex', 'w-full', 'flex-col', 'gap-2'] : ['flex', { 'flex-col': props.horizontal, 'gap-2': !props.horizontal }],
);

const sliderRowClasses = computed(() => ['flex', 'items-center', 'gap-2', { 'w-full': props.fullWidthWithValueBelow }]);

const sliderRootClasses = computed(() => [
    'position-relative',
    'user-select-none',
    'flex',
    'h-5',
    'items-center',
    props.fullWidthWithValueBelow ? 'w-full' : 'w-200',
]);

const valueClasses = computed(() => ['text-small', 'text-muted', { 'mt-1 w-full text-center': props.fullWidthWithValueBelow }]);

const updateModelValue = (value) => {
    // Ensure we have an array with two numbers
    const safeValue = Array.isArray(value) ? value : [0, props.max];

    const cleanValue = [
        typeof safeValue[0] === 'number' ? safeValue[0] : props.min,
        typeof safeValue[1] === 'number' ? safeValue[1] : props.min,
    ].sort((a, b) => a - b);

    if (cleanValue.every((value) => !value)) {
        emit('update:modelValue', null);
        return;
    }

    emit('update:modelValue', cleanValue);
};
</script>

<template>
    <form-group :horizontal="horizontal" :label="label">
        <div :class="wrapperClasses">
            <div :class="sliderRowClasses">
                <SliderRoot
                    :max="props.max"
                    :min="props.min"
                    :model-value="sliderModelValue"
                    :step="props.stepSize"
                    :class="sliderRootClasses"
                    @update:model-value="updateModelValue"
                >
                    <SliderTrack class="position-relative h-2 grow rounded bg-secondary opacity-30">
                        <SliderRange class="position-absolute h-100 rounded bg-primary" />
                    </SliderTrack>
                    <SliderThumb aria-label="Start" class="clickable d-block rounded-circle focus-outline-none h-6 w-6 border-2 bg-white" />
                    <SliderThumb aria-label="End" class="clickable d-block rounded-circle focus-outline-none z-10 h-6 w-6 border-2 bg-white" />
                </SliderRoot>
                <jet-button
                    v-if="modelValue"
                    :destructive="true"
                    class="text-small p-0"
                    style="min-width: 0; height: auto"
                    type="button"
                    variant="tertiary"
                    @click.stop="$emit('update:modelValue', null)"
                >
                    {{ trans('general.action.clear') }}
                </jet-button>
            </div>
            <div :class="valueClasses" :style="props.fullWidthWithValueBelow ? undefined : 'min-width: 1.125rem'">
                {{ formatValue(modelValue) }}
            </div>
        </div>
        <div v-if="invalidFeedback" class="text-small text-danger">{{ invalidFeedback }}</div>
    </form-group>
</template>

<style scoped>
.border-2 {
    border: 2px solid #2c54ea;
}
.w-200 {
    width: 200px;
}
.h-5 {
    height: 1.25rem;
}
.h-2 {
    height: 0.5rem;
}
.w-6 {
    width: 1rem;
}
.h-6 {
    height: 1rem;
}
.focus-outline-none:focus {
    outline: none;
}
</style>
